import React, { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';

// Utility to get API base URL depending on environment
const getApiBaseUrl = () => {
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return 'http://localhost:5000';
  }
  // Use relative URL for production
  return '';
};


const Admin = () => {
  const [projects, setProjects] = useState([]);
  const [allTools, setAllTools] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allObjects, setAllObjects] = useState([]);
  const [editStates, setEditStates] = useState({}); // { [projectId]: { ...editState } }
  const [loading, setLoading] = useState(true);
  // Dropdown for publication status
  const [showStatusDropdown, setShowStatusDropdown] = useState(null);

  useEffect(() => {
    // Fetch all projects
    fetch(`${getApiBaseUrl()}/api/projects`)
      .then((res) => res.json())
      .then((data) => {
        setProjects(data);
        // Initialize edit states
        const initialEditStates = {};
        data.forEach((project) => {
          initialEditStates[project.id] = {
            name: project.name,
            description: project.description || '',
            tools: [],
            categories: [],
            objects: [],
            editing: false,
            error: '',
            success: '',
          };
        });
        setEditStates(initialEditStates);
      });
    // Fetch all tools
    fetch(`${getApiBaseUrl()}/api/tools`)
      .then((res) => res.json())
      .then((data) => setAllTools(data.map(t => ({ value: t.id, label: t.name }))));
    // Fetch all categories
    fetch(`${getApiBaseUrl()}/api/categories`)
      .then((res) => res.json())
      .then((data) => setAllCategories(data.map(c => ({ value: c.id, label: c.name }))));
    // Fetch all objects
    fetch(`${getApiBaseUrl()}/api/objects`)
      .then((res) => res.json())
      .then((data) => setAllObjects(data.map(o => ({ value: o.id, label: o.name }))));
    setLoading(false);
  }, []);

  // Fetch project tools, categories, objects, and description when entering edit mode
  const handleEdit = async (project) => {
    const [toolsRes, categoriesRes, objectsRes, projectRes] = await Promise.all([
      fetch(`${getApiBaseUrl()}/api/projects/${project.id}/tools`).then(res => res.json()),
      fetch(`${getApiBaseUrl()}/api/projects/${project.id}/categories`).then(res => res.json()),
      fetch(`${getApiBaseUrl()}/api/projects/${project.id}/objects`).then(res => res.json()),
      fetch(`${getApiBaseUrl()}/api/projects/${project.id}`).then(res => res.json()),
    ]);
    setEditStates((prev) => ({
      ...prev,
      [project.id]: {
        ...prev[project.id],
        tools: toolsRes.map(t => ({ value: t.id, label: t.name })),
        categories: categoriesRes.map(c => ({ value: c.id, label: c.name })),
        objects: objectsRes.map(o => ({ value: o.id, label: o.name })),
        description: projectRes.description || '',
        editing: true,
        error: '',
        success: '',
      },
    }));
  };

  const handleCancel = (project) => {
    setEditStates((prev) => ({
      ...prev,
      [project.id]: {
        ...prev[project.id],
        editing: false,
        error: '',
        success: '',
      },
    }));
  };

  const handleChange = (projectId, field, value) => {
    setEditStates((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        [field]: value,
      },
    }));
  };

  const handleSave = async (project) => {
    setEditStates((prev) => ({
      ...prev,
      [project.id]: {
        ...prev[project.id],
        error: '',
        success: '',
      },
    }));
    const token = localStorage.getItem('token');
    const body = {
      name: editStates[project.id].name,
      description: editStates[project.id].description,
      tools: editStates[project.id].tools.map(t => t.value),
      categories: editStates[project.id].categories.map(c => c.value),
      objects: editStates[project.id].objects.map(o => o.value),
    };
    const res = await fetch(`${getApiBaseUrl()}/api/projects/${project.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setEditStates((prev) => ({
        ...prev,
        [project.id]: {
          ...prev[project.id],
          error: data.error || 'Erreur lors de la mise à jour',
        },
      }));
    } else {
      setEditStates((prev) => ({
        ...prev,
        [project.id]: {
          ...prev[project.id],
          success: 'Projet mis à jour !',
          editing: false,
        },
      }));
      // Optionally, refresh projects list
      fetch(`${getApiBaseUrl()}/api/projects`)
        .then((res) => res.json())
        .then((data) => setProjects(data));
    }
  };

  if (loading) return <div>Chargement...</div>;

  // Handler for updating project picture
  const handlePictureUpdate = async (projectId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}/image`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    if (!res.ok) {
      setEditStates((prev) => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          error: 'Erreur lors de la mise à jour de l\'image',
        },
      }));
    } else {
      // Refresh projects list
      fetch(`${getApiBaseUrl()}/api/projects`)
        .then((res) => res.json())
        .then((data) => setProjects(data));
    }
  };

  // Handler for deleting a project
  const handleDelete = async (projectId) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!res.ok) {
      alert('Erreur lors de la suppression');
    } else {
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    }
  };

  // Handler for inline name edit
  const handleNameEdit = (projectId) => {
    setEditStates((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        editingName: true,
        tempName: prev[projectId].name,
      },
    }));
  };

  const handleNameChange = (projectId, value) => {
    setEditStates((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        tempName: value,
      },
    }));
  };

  const handleNameSave = async (projectId) => {
    const token = localStorage.getItem('token');
    const name = editStates[projectId].tempName;
    const res = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) {
      setEditStates((prev) => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          error: 'Erreur lors de la mise à jour du nom',
        },
      }));
    } else {
      setEditStates((prev) => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          name,
          editingName: false,
          tempName: '',
        },
      }));
      // Refresh projects list
      fetch(`${getApiBaseUrl()}/api/projects`)
        .then((res) => res.json())
        .then((data) => setProjects(data));
    }
  };

  const handleNameCancel = (projectId) => {
    setEditStates((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        editingName: false,
        tempName: '',
      },
    }));
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <h1 style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, marginBottom: 24 }}>Gestion des projets</h1>
      {projects.length === 0 && <p>Aucun projet trouvé.</p>}
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead>
          <tr style={{ background: '#f5f5f5' }}>
            <th style={{ padding: 10, border: '1px solid #eee', minWidth: 40 }}>Statut</th>
            <th style={{ padding: 10, border: '1px solid #eee', minWidth: 180 }}>Nom du projet</th>
            <th style={{ padding: 10, border: '1px solid #eee', minWidth: 120 }}>Image</th>
            <th style={{ padding: 10, border: '1px solid #eee', minWidth: 100 }}>Likes</th>
            <th style={{ padding: 10, border: '1px solid #eee', minWidth: 100 }}>Difficulté</th>
            <th style={{ padding: 10, border: '1px solid #eee', minWidth: 120 }}>Modifier</th>
            <th style={{ padding: 10, border: '1px solid #eee', minWidth: 120 }}>Supprimer</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => {
            // Determine flag color based on publication_status
            let flagColor = '#aaa';
            if (project.publication_status === 'published') flagColor = '#2ecc40';
            else if (project.publication_status === 'flagged') flagColor = '#f39c12';
            else if (project.publication_status === 'removed') flagColor = '#e74c3c';

            // Handler to update publication_status
            const handleStatusUpdate = async (projectId, newStatus) => {
              const token = localStorage.getItem('token');
              const res = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}/publication_status`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ publication_status: newStatus }),
              });
              if (res.ok) {
                // Update UI
                setProjects((prev) => prev.map((p) => p.id === projectId ? { ...p, publication_status: newStatus } : p));
              }
              setShowStatusDropdown(null);
            };

            return (
            <tr key={project.id}>
              {/* Publication Status Flag */}
              <td style={{ border: '1px solid #eee', padding: 10, textAlign: 'center', position: 'relative' }}>
                <img
                  src={process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/assets/site/flag.svg' : '/assets/site/flag.svg'}
                  alt={project.publication_status}
                  style={{ width: 28, height: 28, filter: `drop-shadow(0 0 4px ${flagColor})`, color: flagColor, fill: flagColor, WebkitFilter: `drop-shadow(0 0 4px ${flagColor})`, cursor: 'pointer' }}
                  className="status-flag"
                  onClick={() => setShowStatusDropdown(project.id)}
                />
                {showStatusDropdown === project.id && (
                  <div style={{ position: 'absolute', top: 38, left: '50%', transform: 'translateX(-50%)', background: '#fff', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10, minWidth: 120 }}>
                    {[{ value: 'published', label: 'Publié', color: '#2ecc40' }, { value: 'flagged', label: 'Signalé', color: '#f39c12' }, { value: 'removed', label: 'Supprimé', color: '#e74c3c' }].map(opt => (
                      <div key={opt.value} style={{ padding: '8px 12px', cursor: 'pointer', color: opt.color, fontWeight: project.publication_status === opt.value ? 700 : 400, background: project.publication_status === opt.value ? '#f5f5f5' : '#fff' }} onClick={() => handleStatusUpdate(project.id, opt.value)}>
                        <span style={{ marginRight: 8, display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: opt.color, verticalAlign: 'middle' }}></span>
                        {opt.label}
                      </div>
                    ))}
                    <div style={{ padding: '6px 0', textAlign: 'center', color: '#888', cursor: 'pointer', fontSize: 13 }} onClick={() => setShowStatusDropdown(null)}>Annuler</div>
                  </div>
                )}
              </td>
              {/* Project Name (editable inline) */}
              <td style={{ border: '1px solid #eee', padding: 10, fontWeight: 600, fontSize: 17 }}>
                {editStates[project.id]?.editingName ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="text"
                      value={editStates[project.id].tempName}
                      onChange={e => handleNameChange(project.id, e.target.value)}
                      style={{ fontSize: 16, padding: 4, borderRadius: 4, border: '1px solid #ccc' }}
                    />
                    <button style={{ background: '#2ecc40', color: 'white', border: 'none', borderRadius: 4, padding: '4px 10px', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleNameSave(project.id)}>OK</button>
                    <button style={{ background: '#eee', color: '#333', border: 'none', borderRadius: 4, padding: '4px 10px', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleNameCancel(project.id)}>Annuler</button>
                  </div>
                ) : (
                  <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleNameEdit(project.id)}>{project.name}</span>
                )}
                {editStates[project.id]?.error && <div style={{ color: 'red', marginTop: 4 }}>{editStates[project.id].error}</div>}
              </td>
              {/* Project Picture (click to update, always show image-pen.svg) */}
              <td style={{ border: '1px solid #eee', padding: 10 }}>
                <label style={{ cursor: 'pointer' }}>
                  <img
                    src={process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/assets/site/image-pen.svg' : '/assets/site/image-pen.svg'}
                    alt="Modifier l'image"
                    style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8, border: '1px solid #ccc', background: '#f8f8f8', padding: 6 }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={e => {
                      if (e.target.files && e.target.files[0]) {
                        handlePictureUpdate(project.id, e.target.files[0]);
                      }
                    }}
                  />
                </label>
              </td>
              {/* Likes (editable) */}
              <td style={{ border: '1px solid #eee', padding: 10, textAlign: 'center' }}>
                {editStates[project.id]?.editingLikes ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                    <input
                      type="number"
                      min={0}
                      value={editStates[project.id].tempLikes}
                      onChange={e => {
                        const val = Math.max(0, Number(e.target.value));
                        setEditStates(prev => ({
                          ...prev,
                          [project.id]: {
                            ...prev[project.id],
                            tempLikes: val,
                          },
                        }));
                      }}
                      style={{ width: 60, fontSize: 15, padding: 2, borderRadius: 4, border: '1px solid #ccc' }}
                    />
                    <button style={{ background: '#2ecc40', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontWeight: 600, cursor: 'pointer' }} onClick={async () => {
                      const token = localStorage.getItem('token');
                      const likes = editStates[project.id].tempLikes;
                      const res = await fetch(`${getApiBaseUrl()}/api/projects/${project.id}/likes`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ likes }),
                      });
                      if (!res.ok) {
                        setEditStates(prev => ({
                          ...prev,
                          [project.id]: {
                            ...prev[project.id],
                            error: 'Erreur lors de la mise à jour des likes',
                          },
                        }));
                      } else {
                        setEditStates(prev => ({
                          ...prev,
                          [project.id]: {
                            ...prev[project.id],
                            editingLikes: false,
                            tempLikes: '',
                          },
                        }));
                        // Refresh projects list
                        fetch(`${getApiBaseUrl()}/api/projects`)
                          .then((res) => res.json())
                          .then((data) => setProjects(data));
                      }
                    }}>OK</button>
                    <button style={{ background: '#eee', color: '#333', border: 'none', borderRadius: 4, padding: '2px 8px', fontWeight: 600, cursor: 'pointer' }} onClick={() => setEditStates(prev => ({
                      ...prev,
                      [project.id]: {
                        ...prev[project.id],
                        editingLikes: false,
                        tempLikes: '',
                      },
                    }))}>Annuler</button>
                  </div>
                ) : (
                  <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setEditStates(prev => ({
                    ...prev,
                    [project.id]: {
                      ...prev[project.id],
                      editingLikes: true,
                      tempLikes: project.likes || 0,
                    },
                  }))}>{project.likes || 0}</span>
                )}
              </td>
              {/* Difficulty (editable) */}
              <td style={{ border: '1px solid #eee', padding: 10, textAlign: 'center' }}>
                {editStates[project.id]?.editingDifficulty ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                    <input
                      type="number"
                      min={1}
                      max={5}
                      value={editStates[project.id].tempDifficulty}
                      onChange={e => {
                        let val = Number(e.target.value);
                        if (val < 1) val = 1;
                        if (val > 5) val = 5;
                        setEditStates(prev => ({
                          ...prev,
                          [project.id]: {
                            ...prev[project.id],
                            tempDifficulty: val,
                          },
                        }));
                      }}
                      style={{ width: 60, fontSize: 15, padding: 2, borderRadius: 4, border: '1px solid #ccc' }}
                    />
                    <button style={{ background: '#2ecc40', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontWeight: 600, cursor: 'pointer' }} onClick={async () => {
                      const token = localStorage.getItem('token');
                      const difficulty = editStates[project.id].tempDifficulty;
                      const res = await fetch(`${getApiBaseUrl()}/api/projects/${project.id}/difficulty`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ difficulty }),
                      });
                      if (!res.ok) {
                        setEditStates(prev => ({
                          ...prev,
                          [project.id]: {
                            ...prev[project.id],
                            error: 'Erreur lors de la mise à jour de la difficulté',
                          },
                        }));
                      } else {
                        setEditStates(prev => ({
                          ...prev,
                          [project.id]: {
                            ...prev[project.id],
                            editingDifficulty: false,
                            tempDifficulty: '',
                          },
                        }));
                        // Refresh projects list
                        fetch(`${getApiBaseUrl()}/api/projects`)
                          .then((res) => res.json())
                          .then((data) => setProjects(data));
                      }
                    }}>OK</button>
                    <button style={{ background: '#eee', color: '#333', border: 'none', borderRadius: 4, padding: '2px 8px', fontWeight: 600, cursor: 'pointer' }} onClick={() => setEditStates(prev => ({
                      ...prev,
                      [project.id]: {
                        ...prev[project.id],
                        editingDifficulty: false,
                        tempDifficulty: '',
                      },
                    }))}>Annuler</button>
                  </div>
                ) : (
                  <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setEditStates(prev => ({
                    ...prev,
                    [project.id]: {
                      ...prev[project.id],
                      editingDifficulty: true,
                      tempDifficulty: project.difficulty || 1,
                    },
                  }))}>{project.difficulty || 1}</span>
                )}
              </td>
              {/* Edit Button (tools, categories, steps) */}
              <td style={{ border: '1px solid #eee', padding: 10 }}>
                {!editStates[project.id]?.editing ? (
                  <button style={{ background: '#2ecc40', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleEdit(project)}>Modifier</button>
                ) : (
                  <div style={{ background: '#fafafa', borderRadius: 8, padding: 16, marginTop: 10, maxWidth: 400 }}>
                    <label style={{ fontWeight: 600 }}>Outils utilisés</label>
                    <CreatableSelect
                      isMulti
                      options={allTools}
                      value={editStates[project.id].tools}
                      onChange={val => handleChange(project.id, 'tools', val)}
                      placeholder="Rechercher ou ajouter un outil"
                      styles={{ container: base => ({ ...base, width: '100%' }) }}
                    />
                    <label style={{ fontWeight: 600, marginTop: 10 }}>Catégorie(s)</label>
                    <Select
                      isMulti
                      options={allCategories}
                      value={editStates[project.id].categories}
                      onChange={val => handleChange(project.id, 'categories', val)}
                      placeholder="Rechercher et sélectionner des catégories"
                      styles={{ container: base => ({ ...base, width: '100%' }) }}
                    />
                    <label style={{ fontWeight: 600, marginTop: 10 }}>Objets concernés</label>
                    <Select
                      isMulti
                      options={allObjects}
                      value={editStates[project.id].objects}
                      onChange={val => handleChange(project.id, 'objects', val)}
                      placeholder="Rechercher et sélectionner des objets"
                      styles={{ container: base => ({ ...base, width: '100%' }) }}
                    />
                    <label style={{ fontWeight: 600, marginTop: 10 }}>Etapes de réalisation (1 étape par ligne)</label>
                    <textarea
                      value={editStates[project.id].description}
                      onChange={e => handleChange(project.id, 'description', e.target.value)}
                      style={{ width: '100%', minHeight: 80, padding: 8, marginBottom: 10, borderRadius: 6, border: '1px solid #ccc', fontSize: 15 }}
                    />
                    <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                      <button style={{ flex: 1, background: '#2ecc40', color: 'white', border: 'none', borderRadius: 6, padding: '8px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer' }} onClick={() => handleSave(project)}>Enregistrer</button>
                      <button style={{ flex: 1, background: '#eee', color: '#333', border: 'none', borderRadius: 6, padding: '8px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer' }} onClick={() => handleCancel(project)}>Annuler</button>
                    </div>
                    {editStates[project.id]?.error && <div style={{ color: 'red', marginTop: 8 }}>{editStates[project.id].error}</div>}
                    {editStates[project.id]?.success && <div style={{ color: 'green', marginTop: 8 }}>{editStates[project.id].success}</div>}
                  </div>
                )}
              </td>
              {/* Delete Button */}
              <td style={{ border: '1px solid #eee', padding: 10 }}>
                <button style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleDelete(project.id)}>Supprimer</button>
              </td>
            </tr>
          );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Admin;
